/* eslint-disable no-param-reassign */
import Phaser from 'phaser';
import gameData from '../gameData';
import MapGrid from './MapGrid';
import ABCyaPhaser from '@abcya/abcya_phaser';

export default class GameMap extends Phaser.GameObjects.Container {
  /**
   * 
   * @param {Phaser.Scene} scene 
   */
  constructor(scene) {
    super(scene);

    this.mapWidth = null;
    this.mapHeight = null;
    this.mapGrid = null;
    this.obstacleTypes = null;
    this.startBlock = null;
    this.endBlock = null;

    this.landMasses = [];
    this.landMarks = [];
    this.obstacles = [];

    this.landContainer = new Phaser.GameObjects.Container(this.scene);
    this.markContainer = new Phaser.GameObjects.Container(this.scene);
    this.obstacleContainer = new Phaser.GameObjects.Container(this.scene);

    this.add([this.landContainer, this.markContainer, this.obstacleContainer]);
  }

  /**
   * 
   * @param {{ destinationId: string,map: string,bestTime: number,medal: string,baseType: string }} levelData 
   */
  setUpMap (levelData) {
    this.mapGrid = new MapGrid(gameData.gameConfigs[gameData.game].mapData.maps[levelData.map]);
    const gridSize = gameData.gameConfigs[gameData.game].GRID_SIZE;
    this.mapHeight = this.mapGrid.blocks.length * gridSize;
    this.mapWidth = this.mapGrid.blocks[0].length * gridSize;

    for (let i = 0; i < this.mapGrid.blocks.length; i++) {
      const blockRow = this.mapGrid.blocks[i];
      for (let j = 0; j < blockRow.length; j++) {
        const blockData = blockRow[j];
        let layer;
        let arr;

        if (blockData.blockType !== 'empty' && !blockData.filled) {
          let blockSprite = null;
          let blockXOffset = 0;
          let blockYOffset = 0;
          if (blockData.blockType === 'land') {
            blockSprite = new Phaser.GameObjects.Image(this.scene, 0,0,ABCyaPhaser.Images.DEFAULT_ATLAS, 'small-island').setOrigin(0);
            arr = this.landMasses;
            layer = this.landContainer;
            blockXOffset = (gridSize / 2) - (blockSprite.width / 2);
            blockYOffset = (gridSize / 2) - (blockSprite.height / 2);
          }
          else if (blockData.blockType === 'landmark') {
            // add big island and landmark
            blockSprite = new Phaser.GameObjects.Container(this.scene);

            let baseClip;
            if (levelData.baseType !== '') baseClip = new Phaser.GameObjects.Image(this.scene, 0,0,ABCyaPhaser.Images.DEFAULT_ATLAS, levelData.baseType).setOrigin(0);
            let atlas = ABCyaPhaser.Images.DEFAULT_ATLAS;
            switch (levelData.destinationId.toLowerCase()) {
              case 'bank':
              case 'fire department':
              case 'library':
              case 'police station':
              case 'post office':
              case 'school':
              case 'shopping mall':
              case 'supermarket':
                atlas = ABCyaPhaser.Images.LOCALIZATION_ATLAS;
            }
            const markClip = new Phaser.GameObjects.Image(this.scene, 0,0,atlas, levelData.destinationId.toLowerCase()).setOrigin(0.5, 0);
            if (baseClip) blockSprite.add(baseClip);
            blockSprite.add(markClip);
            if (baseClip) {
              markClip.x = baseClip.width / 2;
              markClip.y = baseClip.height - markClip.height - 40;
            }

            const markArea = this.mapGrid.getAreaBlock(blockData);
            this.mapGrid.fillMapSpace(markArea,baseClip);

            // fill all other landmark spots
            arr = this.landMarks;
            layer = this.markContainer;
            if (baseClip) {
              blockXOffset = ((gridSize * markArea[0].length) / 2) - (baseClip.width / 2);
              blockYOffset = ((gridSize * markArea.length) / 2) - (baseClip.height / 2);
            }
            else {
              blockXOffset = ((gridSize * markArea[0].length) / 2) - (markClip.width / 2);
              blockYOffset = ((gridSize * markArea.length) / 2) - (markClip.height / 2);
            }
          }
          else if (blockData.blockType.indexOf('obstacle')) {
            const obstacleDirection = blockData.blockType.substring(0,blockData.blockType.indexOf(' '));
            let obstacleSpace;
            if (obstacleDirection === 'horizontal') {
              obstacleSpace = this.mapGrid.checkRow(blockData);
            }
            else if (obstacleDirection === 'vertical') {
              obstacleSpace = this.mapGrid.checkColumn(blockData);
            }

            const obstacleData = this.chooseObstacle(obstacleSpace.length);
            const spriteName = obstacleData.multidirectional ? obstacleData.obstacleId : `${obstacleData.obstacleId}-${obstacleDirection}`;
            blockSprite = new Phaser.GameObjects.Image(this.scene, 0,0,ABCyaPhaser.Images.DEFAULT_ATLAS, spriteName).setOrigin(0);

            // shorten obstacle space to match what is used by selected obstacle, so the right amount of squares are filled in grid
            while (obstacleSpace.length > obstacleData.obstacleSize) {
              obstacleSpace.pop();
            }

            arr = this.obstacles;
            layer = this.obstacleContainer;
            blockXOffset = blockData.blockType === 'horizontal obstacle' ? ((gridSize * obstacleSpace.length) / 2) - ((blockSprite.width * blockSprite.scaleX) / 2) : (gridSize / 2) - ((blockSprite.width * blockSprite.scaleX) / 2);
            blockYOffset = blockData.blockType === 'vertical obstacle' ? ((gridSize * obstacleSpace.length) / 2) - (blockSprite.height / 2) - 10 : (gridSize / 2) - (blockSprite.height / 2);

            this.mapGrid.fillMapSpace(obstacleSpace,blockSprite);
          }

          if (blockSprite !== null) {
            blockData.filled = true;
            blockData.sprite = blockSprite;
            blockSprite.x = (gridSize * blockData.columnIndex) + blockXOffset;
            blockSprite.y = (gridSize * blockData.rowIndex) + blockYOffset;
            layer.add(blockSprite);
            const mapBlock = { sprite: blockSprite,gridInfo: blockData };
            arr.push(mapBlock);
          }
        }

        if (blockData.isStart) this.startBlock = blockData;
        if (blockData.isEnd) this.endBlock = blockData;
      }
    }

    this.shortestPath = this.getShortestPath();

    this.emit('MAP_SET_UP_COMPLETE');
  }

  chooseObstacle (gridSpace) {
    const obstacleList = gameData.gameConfigs[gameData.game].mapData.obstacles;
    const newObstacle = obstacleList[Phaser.Math.Between(0,obstacleList.length - 1)];

    if (newObstacle.obstacleSize <= gridSpace) {
      return newObstacle;
    }
    else {
      return this.chooseObstacle(gridSpace);
    }
  }

  getShortestPath (path, open, closed) {
    path = path || [this.startBlock];
    open = open || [];
    closed = closed || [];

    // get current end of path
    // find all adjacent
    // if they are not empty add them to closed list
    // if they are open add to open list if they are not already in it
    // choose lowest movement score and add that to path
    // if all adjacent are blocked, remove cur block from path , place cur block in closed list and continue from last block
    const curBlock = path[path.length - 1];
    const adjacentList = this.getAllAdjacentBlocks(curBlock,closed);

    let smallestCost;
    let nextPathBlock = null;
    let i = adjacentList.length - 1;
    while (i >= 0) {
      const aBlock = adjacentList[i];
      if (aBlock.filled) {
        adjacentList.splice(i,1);
        closed.push(aBlock);
        if (open.indexOf(aBlock) !== -1) open.splice(open.indexOf(aBlock), 1);
      }
      else {
        if (open.indexOf(aBlock) === -1) open.push(aBlock);
        const blockMoveCost = this.getMoveDistance(aBlock, this.endBlock);

        if ((!smallestCost || blockMoveCost < smallestCost) && path.indexOf(aBlock) === -1) {
          smallestCost = blockMoveCost;
          nextPathBlock = aBlock;
        }
      }
      i--;
    }

    if (nextPathBlock === null) {
      path.pop();
      closed.push(curBlock);
      return this.getShortestPath(path,open,closed);
    }
    else {
      path.push(nextPathBlock);

      if (nextPathBlock !== this.endBlock) {
        return this.getShortestPath(path,open,closed);
      }
      else {
        return path;
      }
    }

  }

  getAllAdjacentBlocks (centerBlock,closedList) {
    const adjacents = [];
    const startX = centerBlock.columnIndex;
    const startY = centerBlock.rowIndex;

    // same column
    if (startY > 0) {
      const upBlock = this.mapGrid.getBlockByCoordinates(startY - 1, startX);
      if (closedList.indexOf(upBlock) === -1) adjacents.push(upBlock);
    }
    if (startY < this.mapGrid.blocks.length - 1) {
      const downBlock = this.mapGrid.getBlockByCoordinates(startY + 1,startX);
      if (closedList.indexOf(downBlock) === -1) adjacents.push(downBlock);
    }

    // left side
    if (startX > 0) {
      const left = this.mapGrid.getBlockByCoordinates(startY,startX - 1);
      if (closedList.indexOf(left) === -1) adjacents.push(left);
    }

    // right side
    if (startX < this.mapGrid.blocks[0].length - 1) {
      const right = this.mapGrid.getBlockByCoordinates(startY,startX + 1);
      if (closedList.indexOf(right) === - 1) adjacents.push(right);
    }

    return adjacents;
  }

  getMoveDistance (fromBlock,toBlock) {
    const xDist = Math.abs(fromBlock.columnIndex - toBlock.columnIndex);
    const yDist = Math.abs(fromBlock.rowIndex - toBlock.rowIndex);

    return xDist + yDist;
  }

  getBlockByLocation (locPt) {
    let locationBlock;
    let locXIndex = -1;
    let locYIndex = -1;
    const gridSize = gameData.gameConfigs[gameData.game].GRID_SIZE;
    for (let i = 0; i < this.mapGrid.blocks.length; i++) {
      const row = this.mapGrid.blocks[i];
      for (let j = 0; j < row.length; j++) {
        if (gridSize * j > locPt.x) {
          break;
        }
        else {
          locXIndex = j;
        }
      }

      if (gridSize * i > locPt.y) {
        break;
      }
      else {
        locYIndex = i;
      }
    }

    if (locXIndex >= 0 && locYIndex >= 0) locationBlock = this.mapGrid.blocks[locYIndex][locXIndex];
    return locationBlock;
  }

  getBlockXY (block) {
    const gridSize = gameData.gameConfigs[gameData.game].GRID_SIZE;
    const blockX = block.columnIndex * gridSize;
    const blockY = block.rowIndex * gridSize;
    return { x: blockX,y: blockY };
  }

  toggleShowObstacles (toShow) {
    this.obstacleContainer.alpha = toShow ? 1 : 0;
  }

  getPathDirection (startBlock) {
    let pathDirection;

    // find direction of next block in path
    // if current block is not on best path, find a new one
    const pathForOrientation = this.shortestPath.indexOf(startBlock) === -1 ? this.getShortestPathFrom(startBlock) : this.shortestPath;
    const startBlockIndex = pathForOrientation.indexOf(startBlock);
    const nextBlock = pathForOrientation[startBlockIndex + 1];
    if (startBlockIndex === pathForOrientation.length - 1) return 'up';
    if (nextBlock.rowIndex < startBlock.rowIndex) {
      if (nextBlock.columnIndex === startBlock.columnIndex) {
        pathDirection = 'up';
      }
      else {
        pathDirection = nextBlock.columnIndex < startBlock.columnIndex ? 'upleft' : 'upright';
      }

    }
    else if (nextBlock.rowIndex > startBlock.rowIndex) {
      if (nextBlock.columnIndex === startBlock.columnIndex) {
        pathDirection = 'down';
      }
      else {
        pathDirection = nextBlock.columnIndex < startBlock.columnIndex ? 'downleft' : 'downright';
      }
    }
    else {
      if (nextBlock.columnIndex > startBlock.columnIndex) pathDirection = 'right';
      if (nextBlock.columnIndex < startBlock.columnIndex) pathDirection = 'left';
    }

    return pathDirection;
  }

  getShortestPathFrom (start,path, open, closed) {
    path = path || [start];
    open = open || [];
    closed = closed || [];

    // get current end of path
    // find all adjacent
    // if they are not empty add them to closed list
    // if they are open add to open list if they are not already in it
    // choose lowest movement score and add that to path
    // if all adjacent are blocked, remove cur block from path , place cur block in closed list and continue from last block
    const curBlock = path[path.length - 1];
    const adjacentList = this.getAllAdjacentBlocks(curBlock,closed);

    let smallestCost;
    let nextPathBlock = null;
    let i = adjacentList.length - 1;
    while (i >= 0) {
      const aBlock = adjacentList[i];
      if (aBlock.filled) {
        adjacentList.splice(i,1);
        closed.push(aBlock);
        if (open.indexOf(aBlock) !== -1) open.splice(open.indexOf(aBlock), 1);
      }
      else {
        if (open.indexOf(aBlock) === -1) open.push(aBlock);
        const blockMoveCost = this.getMoveDistance(aBlock, this.endBlock);

        if ((!smallestCost || blockMoveCost < smallestCost) && path.indexOf(aBlock) === -1) {
          smallestCost = blockMoveCost;
          nextPathBlock = aBlock;
        }
      }
      i--;
    }

    if (nextPathBlock === null) {
      path.pop();
      closed.push(curBlock);
      return this.getShortestPath(path,open,closed);
    }
    else {
      path.push(nextPathBlock);

      if (nextPathBlock !== this.endBlock) {
        return this.getShortestPath(path,open,closed);
      }
      else {
        return path;
      }
    }
  }
}